import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import { Maybe, rd } from '@passionware/monads';
import { Calendar } from 'lucide-react';
import {
  createDateFilter,
  dateFilterSchemaOf,
  GenericDateFilter
} from 'v5/api/_common/query/filters/GenericDateFilter';
import { FilterDateConfig } from 'v5/patterns/filters/FilterWidget.types';
import { convertDateFilterToApi } from 'v5/platform/filters/filterConverters';
import { TFunction } from 'i18next';

const scheduledFilterPresetIds = [
  'today',
  'tomorrow',
  'nextWeek',
  'nextMonth'
] as const;

export type ScheduledFilterPreset = (typeof scheduledFilterPresetIds)[number];

export type ScheduledFilter = GenericDateFilter<ScheduledFilterPreset>;

function resolveRange(
  preset: ScheduledFilterPreset,
  today: CalendarDate
): {
  from: CalendarDate;
  to: CalendarDate;
} {
  switch (preset) {
    case 'today':
      return {
        from: today,
        to: today
      };
    case 'tomorrow':
      return {
        from: today.add({ days: 1 }),
        to: today.add({ days: 1 })
      };
    case 'nextWeek':
      const nextWeekStart = today
        .add({ weeks: 1 })
        .subtract({ days: today.day - 1 });
      const nextWeekEnd = nextWeekStart.add({ days: 6 });
      return {
        from: nextWeekStart,
        to: nextWeekEnd
      };
    case 'nextMonth':
      const nextMonth = today.add({ months: 1 });
      const nextMonthStart = nextMonth.subtract({ days: nextMonth.day - 1 });
      const nextMonthEnd = nextMonthStart
        .add({ months: 1 })
        .subtract({ days: 1 });
      return {
        from: nextMonthStart,
        to: nextMonthEnd
      };
  }
}

const createRangeMatcher =
  (preset: ScheduledFilterPreset) =>
  (date: CalendarDate, now: CalendarDate) => {
    const { from, to } = resolveRange(preset, now);
    return date.compare(from) >= 0 && date.compare(to) <= 0;
  };

function createScheduledFilter(
  label: string,
  t: TFunction<'core'>
): FilterDateConfig<ScheduledFilterPreset> {
  return {
    label,
    icon: <Calendar />,
    type: 'date',
    createDefaultValue: () => ({ operator: 'preset', value: 'today' }),
    options: {
      useOptions() {
        return rd.of(
          scheduledFilterPresetIds.map(preset => ({
            value: preset,
            label: t(`standard-date-filter.scheduled.${preset}`)
          }))
        );
      },
      useResolver() {
        return rd.of(
          createDateFilter(
            Object.fromEntries(
              scheduledFilterPresetIds.map(preset => [
                preset,
                {
                  match: createRangeMatcher(preset)
                }
              ])
            )
          )
        );
      }
    }
  };
}

function createScheduledHttpConverter() {
  return (filter: Maybe<ScheduledFilter>) => {
    return filter
      ? convertDateFilterToApi<ScheduledFilterPreset>(filter, {
          resolvePreset: preset =>
            resolveRange(preset, today(getLocalTimeZone()))
        })
      : null;
  };
}

const scheduledFilterSchema = dateFilterSchemaOf([
  'today',
  'tomorrow',
  'nextWeek',
  'nextMonth'
]);

export const scheduledFilter = {
  createFilterConfig: createScheduledFilter,
  createHttpConverter: createScheduledHttpConverter,
  resolveRange: resolveRange,
  schema: scheduledFilterSchema
};
